import toast from "@/components/ui/toast";
import {
  CreateFeed,
  DeleteFeed,
  GetAllNewsFeeds,
  UpdateFeed,
} from "@/data-access/news-feeds/queries/news-feeds-queries";
import { client } from "@/lib/urqlProvider";
import type { NewsFeed, NewsFeedsSlice } from "./news-feeds.slice";
import useNewsFeedsStore from "./news-feeds.slice";

// Variable types derived from GraphQL operations
type CreateFeedVariablesType = Parameters<
  Exclude<typeof CreateFeed.__apiType, undefined>
>[0];

type UpdateFeedVariablesType = Parameters<
  Exclude<typeof UpdateFeed.__apiType, undefined>
>[0];

type DeleteFeedVariablesType = Parameters<
  Exclude<typeof DeleteFeed.__apiType, undefined>
>[0];

/**
 * Fetches all news feeds from the server
 */
export const fetchNewsFeeds = async () => {
  useNewsFeedsStore.setState({ isFetchingNewsFeeds: true });

  try {
    const result = await client.query(GetAllNewsFeeds, {}).toPromise();

    if (result.error) {
      toast.error("Failed to fetch news feeds.");
      useNewsFeedsStore.setState(
        { isFetchingNewsFeeds: false },
        false,
        "fetch news feeds error",
      );
      return;
    }

    if (!result.data?.feeds) {
      toast.error("Failed to fetch news feeds.");
      useNewsFeedsStore.setState({ isFetchingNewsFeeds: false });
      return;
    }

    useNewsFeedsStore.setState(
      {
        allNewsFeeds: result.data.feeds,
        isFetchingNewsFeeds: false,
      },
      false,
      "fetch news feeds success",
    );
  } catch (error) {
    console.error("Error fetching news feeds:", error);
    toast.error("An error occurred while fetching news feeds.");
    useNewsFeedsStore.setState(
      { isFetchingNewsFeeds: false },
      false,
      "fetch news feeds error",
    );
  }
};

/**
 * Creates a new news feed
 */
export const createNewsFeed = async (
  data: CreateFeedVariablesType["data"],
  set: (
    partial:
      | Partial<NewsFeedsSlice>
      | ((state: NewsFeedsSlice) => Partial<NewsFeedsSlice>),
  ) => void,
): Promise<NewsFeed | undefined> => {
  set({ isCreatingFeed: true });

  try {
    const result = await client.mutation(CreateFeed, { data }).toPromise();

    if (result.error) {
      toast.error("Failed to create news feed.");
      set({ isCreatingFeed: false });
      return undefined;
    }

    const createFeed = result.data?.createFeed;
    if (!createFeed || createFeed.__typename !== "FeedType") {
      toast.error("Failed to create news feed.");
      set({ isCreatingFeed: false });
      return undefined;
    }

    const newFeed: NewsFeed = {
      id: createFeed.id,
      name: createFeed.name,
      image: null,
      terms: createFeed.terms || [],
      knownTags: createFeed.knownTags || [],
    };

    set((state: NewsFeedsSlice) => ({
      allNewsFeeds: [...state.allNewsFeeds, newFeed],
      isCreatingFeed: false,
      createFeedState: "complete",
      // Close panel and clear form state if it was open for creating
      ...(state.panelMode === "add" && {
        isPanelOpen: false,
        panelMode: null,
        formFeedName: "",
        formKeyTerms: [],
      }),
    }));

    toast({
      type: "success",
      title: "News feed created!",
      description: `"${createFeed.name}" has been created successfully.`,
    });

    return newFeed;
  } catch (error) {
    console.error("Error creating news feed:", error);
    toast.error("An error occurred while creating the news feed.");
    set({ isCreatingFeed: false });
    return undefined;
  }
};

/**
 * Updates an existing news feed
 */
export const updateNewsFeed = async (
  data: UpdateFeedVariablesType["data"],
  feedId: string, // ID in the app; Int! at the mutation boundary
  set: (
    partial:
      | Partial<NewsFeedsSlice>
      | ((state: NewsFeedsSlice) => Partial<NewsFeedsSlice>),
  ) => void,
): Promise<boolean> => {
  set({ isUpdatingFeed: true });

  try {
    const result = await client
      .mutation(UpdateFeed, { data, feedId: Number(feedId) })
      .toPromise();

    if (result.error) {
      toast.error("Failed to update news feed.");
      set({ isUpdatingFeed: false });
      return false;
    }

    const updateFeed = result.data?.updateFeed;
    if (!updateFeed || updateFeed.__typename !== "FeedType") {
      toast.error("Failed to update news feed.");
      set({ isUpdatingFeed: false });
      return false;
    }

    set((state: NewsFeedsSlice) => ({
      allNewsFeeds: state.allNewsFeeds.map((feed: NewsFeed) =>
        feed.id === feedId
          ? {
              ...feed,
              id: updateFeed.id || feed.id,
              name: updateFeed.name,
              terms: updateFeed.terms || [],
              knownTags: updateFeed.knownTags || [],
            }
          : feed,
      ),
      isUpdatingFeed: false,
      updateFeedState: "complete",
      // Close panel and clear editing state if it was open for editing
      ...(state.panelMode === "edit" && {
        isPanelOpen: false,
        panelMode: null,
        editingFeedId: null,
        formFeedName: "",
        formKeyTerms: [],
      }),
    }));

    toast({
      type: "success",
      title: "News feed updated!",
      description: `"${updateFeed.name}" has been updated successfully.`,
    });

    return true;
  } catch (error) {
    console.error("Error updating news feed:", error);
    toast.error("An error occurred while updating the news feed.");
    set({ isUpdatingFeed: false });
    return false;
  }
};

/**
 * Deletes a news feed
 */
export const deleteNewsFeed = async (
  feedId: DeleteFeedVariablesType["feedId"],
  accepted: DeleteFeedVariablesType["accepted"] = true,
): Promise<boolean> => {
  useNewsFeedsStore.setState({ isDeletingFeed: true });

  try {
    const result = await client
      .mutation(DeleteFeed, { feedId, accepted })
      .toPromise();

    if (result.error) {
      toast.error("Failed to delete news feed.");
      useNewsFeedsStore.setState(
        { isDeletingFeed: false },
        false,
        "delete news feed error",
      );
      return false;
    }

    const deleteFeed = result.data?.deleteFeed;
    if (!deleteFeed) {
      toast.error("Failed to delete news feed.");
      useNewsFeedsStore.setState({ isDeletingFeed: false });
      return false;
    }

    // Handle different response types
    if (deleteFeed.__typename === "SuccessResponse") {
      useNewsFeedsStore.setState(
        (state) => ({
          allNewsFeeds: state.allNewsFeeds.filter(
            (feed) => String(feed.id) !== feedId,
          ),
          isDeletingFeed: false,
        }),
        false,
        "delete news feed success",
      );

      toast({
        type: "success",
        title: "News feed deleted!",
        description: "The news feed has been deleted successfully.",
      });

      return true;
    }

    if (deleteFeed.__typename === "OperationInfo") {
      // Handle operation info messages
      const messages = deleteFeed.messages || [
        { message: "An error occurred while deleting the feed." },
      ];

      for (const { message } of messages) {
        toast.error(message);
      }

      useNewsFeedsStore.setState({ isDeletingFeed: false });
      return false;
    }

    toast.error("Failed to delete news feed.");
    useNewsFeedsStore.setState({ isDeletingFeed: false });
    return false;
  } catch (error) {
    console.error("Error deleting news feed:", error);
    toast.error("An error occurred while deleting the news feed.");
    useNewsFeedsStore.setState({ isDeletingFeed: false });
    return false;
  }
};
